import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Bug,
  Check,
  HelpCircle,
  Lightbulb,
  MessageSquare,
  Send,
  Sparkles,
} from "lucide-react";
import type { FeedbackCategory } from "@mtform/shared";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

// =============================================================================
// FeedbackDialog
//
// One-shot dialog opened from the avatar menu's "Send feedback" item.
// Designed to feel like writing a quick note to the team — not
// submitting a support ticket. Posts to `/api/account/feedback`,
// which lands an email at `service@wisdomweave.com` with the user's
// message plus every piece of debug context we can safely include
// (URL, browser, version — no patient PHI).
//
// UX choices, in case anyone tweaks this later:
//
//   * Categories render as visual chips with icons rather than a
//     dropdown. Faster to scan, more "product" than "form."
//   * The textarea autofocuses on open with a friendly placeholder
//     keyed to the selected category, so the user starts typing
//     without having to read field labels first.
//   * `Cmd/Ctrl + Enter` submits; `Esc` cancels. Standard chat-app
//     muscle memory.
//   * On success, the modal swaps to a confirmation state with a
//     subtle sparkle animation — same visual language as Linear /
//     Vercel / Notion. The "Send another" button stays so a power
//     user can rapid-fire two related notes.
//   * The last picked category persists to localStorage so a user
//     who keeps reporting bugs doesn't have to re-click Bug every
//     time. New users default to Idea (more inviting than Bug).
//   * The dialog deliberately includes the user's email + the page
//     URL in the hint text so the user understands what context the
//     recipient will see. Trust through transparency.
// =============================================================================

const STORAGE_KEY = "rt_sinai_feedback_category_v1";
const MIN_CHARS = 10;
const MAX_CHARS = 2000;

interface FeedbackOption {
  value: FeedbackCategory;
  label: string;
  blurb: string;
  placeholder: string;
  icon: JSX.Element;
}

// Single source of truth for the four categories — keeps the chip
// list, the placeholder selection, and the toast copy in sync.
const OPTIONS: FeedbackOption[] = [
  {
    value: "bug",
    label: "Bug",
    blurb: "Something's broken or behaving oddly.",
    placeholder:
      "What broke? What were you trying to do? Anything you tried that helped or didn't?",
    icon: <Bug className="h-4 w-4 text-rose-600 dark:text-rose-400" />,
  },
  {
    value: "idea",
    label: "Idea",
    blurb: "A feature that'd make your day easier.",
    placeholder:
      "What would make this easier or faster? Even half-formed ideas are welcome — we'll riff on it.",
    icon: <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400" />,
  },
  {
    value: "question",
    label: "Question",
    blurb: "Not sure how something works.",
    placeholder:
      "What are you trying to figure out? We'll come back with an answer + add it to the docs if it's a common one.",
    icon: <HelpCircle className="h-4 w-4 text-primary" />,
  },
  {
    value: "other",
    label: "Other",
    blurb: "Anything else on your mind.",
    placeholder:
      "Praise, gripes, half-baked thoughts, hellos — all welcome. Write whatever.",
    icon: (
      <MessageSquare className="h-4 w-4 text-foreground/70" />
    ),
  },
];

function readStoredCategory(): FeedbackCategory {
  if (typeof window === "undefined") return "idea";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (
      raw === "bug" ||
      raw === "idea" ||
      raw === "question" ||
      raw === "other"
    ) {
      return raw;
    }
  } catch {
    // Private window / disabled storage — fall through to the default.
  }
  return "idea";
}

function writeStoredCategory(value: FeedbackCategory): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* see readStoredCategory */
  }
}

export interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const auth = useAuth();
  const user = auth.state.status === "authenticated" ? auth.state.user : null;

  const [category, setCategory] = useState<FeedbackCategory>(readStoredCategory);
  const [message, setMessage] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Two-phase modal: write → confirm. After a successful submit we
  // swap the body to the confirm state for ~2 s before auto-closing,
  // OR until the user clicks "Send another" / "Close."
  const [confirmed, setConfirmed] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const selected = useMemo(
    () => OPTIONS.find((o) => o.value === category) ?? OPTIONS[1]!,
    [category],
  );

  // Reset every time the dialog opens so the form doesn't carry the
  // previous session's draft into a new submission. The category is
  // re-read from localStorage so the user's last-picked choice
  // surfaces by default (chat-app style).
  useEffect(() => {
    if (!open) return;
    setMessage("");
    setSubmitError(null);
    setConfirmed(false);
    setCategory(readStoredCategory());
    // Focus the textarea on next paint — Radix Dialog's own focus
    // trap normally lands on the first focusable element, but the
    // category chips come before the textarea in the DOM and we want
    // the user typing immediately, not picking a category first.
    const id = window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(id);
  }, [open]);

  // Auto-close 2.5 s after a send, so the user gets the satisfaction of
  // the confirmation without having to dismiss it; they can close earlier
  // via the buttons. Tied to the thank-you screen being up, so "Send
  // another", closing or leaving cancels it: it can't close a second note
  // being typed, or the pop-up opened again within the 2.5 s.
  useEffect(() => {
    if (!open || !confirmed) return;
    const id = window.setTimeout(() => onOpenChange(false), 2500);
    return () => window.clearTimeout(id);
  }, [open, confirmed, onOpenChange]);

  const trimmed = message.trim();
  const canSubmit = trimmed.length >= MIN_CHARS && trimmed.length <= MAX_CHARS;
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_CHARS;
  const overLimit = trimmed.length > MAX_CHARS;

  const mutation = useMutation({
    mutationFn: async () => {
      // Deliberately NOT capturing the page URL / title. The
      // feedback entry lives in the avatar menu and is reachable
      // from anywhere — the route the user happens to be on at
      // click-time has no reliable relationship to what they're
      // writing about. Including it sets up the wrong expectation
      // for the user ("are you tracking me?") without adding
      // signal we can act on. Viewport size is kept because it's
      // a layout-debug useful fact decoupled from "what page",
      // and the browser UA is added server-side from headers.
      const viewportSize =
        typeof window !== "undefined"
          ? `${window.innerWidth}x${window.innerHeight}`
          : undefined;
      return api.account.sendFeedback({
        category,
        message: trimmed,
        viewportSize,
      });
    },
    onSuccess: () => {
      writeStoredCategory(category);
      setConfirmed(true);
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Couldn't send your feedback. Please try again.";
      setSubmitError(msg);
    },
  });

  function handleSubmit() {
    setSubmitError(null);
    if (!canSubmit || mutation.isPending) return;
    mutation.mutate();
  }

  // Cmd+Enter submits. Keep it scoped to the textarea so people
  // navigating between chips with Tab + Enter don't accidentally
  // fire the request.
  function handleTextareaKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  }

  function reset() {
    setMessage("");
    setSubmitError(null);
    setConfirmed(false);
    // Refocus the textarea so the user can rapid-fire follow-up
    // notes without clicking back in.
    window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 50);
  }

  const charCounterTone = overLimit
    ? "text-rose-700 dark:text-destructive"
    : trimmed.length > MAX_CHARS * 0.9
      ? "text-amber-800 dark:text-warning"
      : "text-muted-foreground";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" structured>
        {confirmed ? (
          // Success state — minimal, warm, with a small sparkle so it
          // feels like a moment, not a generic "OK Sent."
          <div className="flex flex-col items-center gap-3 px-5 py-10 text-center animate-fade-up sm:px-6">
            <div className="relative">
              <span className="absolute -inset-3 rounded-full bg-success/20 opacity-70 motion-safe:animate-ping" />
              <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success">
                <Sparkles className="h-7 w-7" strokeWidth={2} />
              </span>
            </div>
            {/* The pop-up's title and description in this state, so it is
                still announced by name. */}
            <DialogTitle className="mt-1">Thanks for the note.</DialogTitle>
            <DialogDescription className="max-w-[34ch]">
              We read every message. If your note needs a reply, we'll
              get back to{" "}
              <span className="font-mono text-foreground/85">
                {user?.email ?? "you"}
              </span>
              .
            </DialogDescription>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              <Button variant="secondary" size="sm" onClick={reset}>
                <MessageSquare className="h-4 w-4" /> Send another
              </Button>
              <Button size="sm" onClick={() => onOpenChange(false)}>
                <Check className="h-4 w-4" /> Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader icon={MessageSquare}>
              <DialogTitle>How's it going?</DialogTitle>
              <DialogDescription>
                Bug reports, feature ideas, questions, or just a hello —
                all welcome. We read every message.
              </DialogDescription>
            </DialogHeader>

            <DialogBody className="space-y-4">
              {/* Category chips. Visual selection, not a dropdown — the
                  four choices fit on one row and pick-by-eye is faster
                  than scan-the-list. */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {OPTIONS.map((opt) => {
                  const active = opt.value === category;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setCategory(opt.value)}
                      className={cn(
                        "flex flex-col items-start gap-1 rounded-xl border px-3 py-2.5 text-left transition",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        active
                          ? "border-primary/55 bg-primary/[0.08]"
                          : "border-border bg-foreground/[0.02] hover:border-primary/40 hover:bg-foreground/[0.05]",
                      )}
                      aria-pressed={active}
                    >
                      <span className="flex items-center gap-1.5">
                        {opt.icon}
                        <span className="text-sm font-bold text-foreground">
                          {opt.label}
                        </span>
                      </span>
                      <span className="text-[0.7rem] leading-snug text-muted-foreground">
                        {opt.blurb}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Message body. Big textarea, friendly placeholder that
                  changes with the selected category, character counter
                  below right-aligned so a long message doesn't push the
                  Send button off-screen. */}
              <div>
                <label
                  htmlFor="feedback-message"
                  className="sr-only"
                >
                  Your message
                </label>
                <textarea
                  id="feedback-message"
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleTextareaKeyDown}
                  placeholder={selected.placeholder}
                  rows={5}
                  maxLength={MAX_CHARS + 200 /* a little overflow so the validator catches it gracefully */}
                  className={cn(
                    "block w-full resize-y rounded-xl border bg-foreground/[0.02] px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground transition",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    overLimit ? "border-destructive/55" : "border-border",
                  )}
                />
                <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">
                    We'll include your email
                    {user?.email ? (
                      <>
                        {" "}
                        <span className="font-mono text-foreground/70">
                          ({user.email})
                        </span>
                      </>
                    ) : null}{" "}
                    so we can get back to you if we have any questions.
                  </span>
                  <span className={cn("font-mono", charCounterTone)}>
                    {trimmed.length}/{MAX_CHARS}
                  </span>
                </div>
              </div>
            </DialogBody>

            {submitError ? (
              <div className="px-5 pb-3 sm:px-6">
                <p
                  role="alert"
                  className="rounded-xl border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs font-semibold text-rose-700 dark:text-destructive"
                >
                  {submitError}
                </p>
              </div>
            ) : null}

            <DialogFooter className="justify-between">
              <span className="text-[0.7rem] text-muted-foreground">
                <kbd className="rounded border border-border bg-foreground/[0.04] px-1 py-0.5 font-mono text-[0.65rem] text-foreground/80">
                  ⌘
                </kbd>{" "}
                +{" "}
                <kbd className="rounded border border-border bg-foreground/[0.04] px-1 py-0.5 font-mono text-[0.65rem] text-foreground/80">
                  Enter
                </kbd>{" "}
                to send
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                  disabled={mutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={!canSubmit || mutation.isPending}
                  title={
                    tooShort
                      ? `Type at least ${MIN_CHARS} characters`
                      : overLimit
                        ? `Trim down to ${MAX_CHARS} characters`
                        : undefined
                  }
                >
                  {mutation.isPending ? (
                    <>
                      <Send className="h-4 w-4 animate-pulse" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" /> Send {selected.label.toLowerCase()}
                    </>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
